import { VIDEO_TEMPLATE_ROUTE } from '@anm/constants/routes';
import { EMBED_INTERNAL_PARAM } from '@anm/constants/wave-editor';
import { VideoTemplatesPageContext } from '@anm/contexts/VideoTemplatesPageContext';
import { AspectRatioLabel } from '@anm/data/aspects';
import { DEFAULT_PAGE } from '@anm/data/video-templates/defaults';
import changeRouter from '@anm/helpers/changeRouter';
import usePageParams from '@anm/hooks/usePageParams';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import routes from '../../../routes';
import { filterActions, filterSelectors } from '../../../store/modules/filter';
import { selectVideoFormats } from '../../../store/modules/video';
import { getTemplateParams } from '../TemplateViewModal/helpers';

import { getCurrentIndex } from './helpers';

const DEFAULT_INDEX = 0;

export const useDropdownHandler = () => {
  const dispatch = useDispatch();
  const { pageName } = useContext(VideoTemplatesPageContext);
  const { embed } = usePageParams();
  const isInternalEmbed = embed === EMBED_INTERNAL_PARAM;
  const [activeItemIndex, setActiveItemIndex] = useState(DEFAULT_INDEX);

  const videoFormats = useSelector(selectVideoFormats);
  const savedFilters = useSelector(filterSelectors.selectSavedFilters);
  const templateFilters = useSelector(filterSelectors.selectTemplatesFilters);
  const searchName = useSelector(filterSelectors.selectSearchName);

  const filters = { ...templateFilters, ...savedFilters };

  const name = filters?.name || searchName;

  useEffect(() => {
    const { aspects } = filters;
    const isMainPage = pageName === DEFAULT_PAGE;

    const newIndex = !aspects || isMainPage ? DEFAULT_INDEX : getCurrentIndex({ aspects, videoFormats });

    setActiveItemIndex(newIndex);

    return () => {
      dispatch(filterActions.updateSavedFilters({}));
      dispatch(filterActions.resetTemplateFilters());
    };
  }, [pageName]);

  useEffect(() => {
    if (!videoFormats.length) return;

    const { aspects } = filters;

    const newIndex = aspects ? getCurrentIndex({ aspects, videoFormats }) : DEFAULT_INDEX;

    setActiveItemIndex(newIndex);
  }, [filters, videoFormats]);

  const isMainPage = pageName === 'main';

  const handleItemClick = useCallback(
    (index: number, aspectLabel: AspectRatioLabel) => {
      const routeName = isMainPage ? 'video-templates' : VIDEO_TEMPLATE_ROUTE;
      const params = getTemplateParams(isMainPage, {
        pageName,
        aspects: aspectLabel,
        ...(name && { name }),
        ...(isInternalEmbed && { embed: EMBED_INTERNAL_PARAM })
      });

      changeRouter({
        routes,
        params,
        routeName,
        isQuery: false
      });
      !aspectLabel &&
        dispatch(
          filterActions.updateSavedFilters({
            aspects: aspectLabel
          })
        );
      setActiveItemIndex(index);
    },
    [pageName, videoFormats, name, filters]
  );

  return [activeItemIndex, handleItemClick] as const;
};
